import React, { Ref } from 'react';
import mergeRefs from 'react-merge-refs';

import { SporsmalHeader } from '../../components/sporsmal-header/sporsmal-header';
import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { KontaktinfoInput } from '../kontaktinfo/kontaktinfo-field';
import { getFeilmeldingFrom, Kontaktinfofelt } from '../kontaktinfo/kontaktinfofelt';
import { updateKontaktInfoInvolvertSvar } from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import { MAKSLENGDER, validateIsNonEmptyText } from '../validation/validate';
import { CurrentKontaktInfoInvolvertSporsmal, validateOptionalField } from './kontaktInfoInvolvert-types';

const isValid = (currentSporsmal: CurrentKontaktInfoInvolvertSporsmal, type: Kontaktinfofelt) => {
    switch (type) {
        case Kontaktinfofelt.Navn:
            return validateIsNonEmptyText(currentSporsmal.navn);
        case Kontaktinfofelt.Mobilnummer:
            return validateOptionalField(currentSporsmal.mobilnummer, Kontaktinfofelt.Mobilnummer);
        case Kontaktinfofelt.Adresse:
            return validateOptionalField(currentSporsmal.adresse, Kontaktinfofelt.Adresse);
        case Kontaktinfofelt.Fodselsdato:
            return validateOptionalField(currentSporsmal.fodselsdato, Kontaktinfofelt.Fodselsnummer);
        default:
            return false;
    }
};
const firstFieldWithError = (currentSporsmal: CurrentKontaktInfoInvolvertSporsmal): Kontaktinfofelt | undefined => {
    const fieldOrder = [
        Kontaktinfofelt.Navn,
        Kontaktinfofelt.Mobilnummer,
        Kontaktinfofelt.Epost,
        Kontaktinfofelt.Fodselsnummer,
        Kontaktinfofelt.Organisasjonsnummer,
        Kontaktinfofelt.Bankkontonummer,
    ];
    return fieldOrder.find((field) => !isValid(currentSporsmal, field));
};

const isFirstFieldWithError = (field: Kontaktinfofelt, currentSporsmal: CurrentKontaktInfoInvolvertSporsmal): boolean =>
    field === firstFieldWithError(currentSporsmal);

interface Props {
    currentSporsmal: CurrentKontaktInfoInvolvertSporsmal;
    showFeilmeldinger: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const KontaktInfoInvolvert = ({ currentSporsmal, showFeilmeldinger, errorRef, focusOnMount = true }: Props) => {
    const dispatch = useSporsmalsflytDispatch();

    const onChange = (value: string, felt: Kontaktinfofelt) => {
        dispatch(updateKontaktInfoInvolvertSvar(felt, value));
    };

    const fornavnRef = useFocusRef<HTMLInputElement>(focusOnMount);

    return (
        <SporsmalHeader tittel={currentSporsmal.sporsmal.tittel}>
            <div>
                <KontaktinfoInput
                    name="navn"
                    value={currentSporsmal.navn}
                    label="Fullt navn"
                    autocomplete="name"
                    errorText={getFeilmeldingFrom(Kontaktinfofelt.Navn)}
                    isValid={!showFeilmeldinger || validateIsNonEmptyText(currentSporsmal.navn)}
                    onChange={(value: string) => {
                        onChange(value, Kontaktinfofelt.Navn);
                    }}
                    itemRef={mergeRefs([
                        fornavnRef,
                        isFirstFieldWithError(Kontaktinfofelt.Navn, currentSporsmal) ? errorRef : undefined,
                    ])}
                />
                <KontaktinfoInput
                    name="mobilnummer"
                    value={currentSporsmal.mobilnummer}
                    label="Mobilnummer (valgfritt)"
                    type="tel"
                    autocomplete="tel"
                    errorText="Vi trenger et norsk mobilnummer (8 siffer)"
                    isValid={
                        !showFeilmeldinger ||
                        validateOptionalField(currentSporsmal.mobilnummer, Kontaktinfofelt.Mobilnummer)
                    }
                    onChange={(value: string) => {
                        onChange(value, Kontaktinfofelt.Mobilnummer);
                    }}
                    itemRef={isFirstFieldWithError(Kontaktinfofelt.Mobilnummer, currentSporsmal) ? errorRef : undefined}
                    small
                />
                <KontaktinfoInput
                    name="addresse"
                    value={currentSporsmal.adresse}
                    label="Adresse (valgfritt)"
                    errorText={`Adresse kan ikke være mer enn ${MAKSLENGDER.lokasjon} tegn.`}
                    isValid={
                        !showFeilmeldinger || validateOptionalField(currentSporsmal.adresse, Kontaktinfofelt.Adresse)
                    }
                    onChange={(value: string) => {
                        onChange(value, Kontaktinfofelt.Adresse);
                    }}
                    itemRef={isFirstFieldWithError(Kontaktinfofelt.Adresse, currentSporsmal) ? errorRef : undefined}
                />
                <KontaktinfoInput
                    name="fodselsdato"
                    value={currentSporsmal.fodselsdato}
                    label="Fødselsdato (valgfritt)"
                    errorText="Vi trenger en gyldig fødselsdato."
                    isValid={
                        !showFeilmeldinger ||
                        validateOptionalField(currentSporsmal.fodselsdato, Kontaktinfofelt.Fodselsdato)
                    }
                    onChange={(value: string) => {
                        onChange(value, Kontaktinfofelt.Fodselsdato);
                    }}
                    itemRef={isFirstFieldWithError(Kontaktinfofelt.Fodselsdato, currentSporsmal) ? errorRef : undefined}
                    placeholder="dd.mm.åååå"
                    small
                />
            </div>
        </SporsmalHeader>
    );
};
